/**
 * WD-T5 — /my-jobs/:id job-scoped sub-dashboard (replaces the dead packet).
 *
 * Product intent (docs/WORKER-DASH-DECISIONS-2026-09-25.md v1.2 §6, Brian):
 * "My Jobs: click → list of jobs they worked on → click a job → filtered
 * sub-dashboard of only the tiles/cards associated with that job." Also PRD
 * §5.3: the job view surfaces documents (full set), JHAs (open + sign),
 * construction plan (view), incidents entry.
 *
 * Sections are gated through roleGate (canSeeTile) exactly like
 * DashboardPage — job scope applies for every role (the point is "the tiles
 * associated with that job"); server scoping (getScopedProject worker-tier
 * ACTIVE-assignment gate) is the security boundary, this is presentation.
 *
 * Every section shows real rows or an honest empty state (why it's empty +
 * how to fix it) — never a bare "None" and never an invented count.
 *
 * Data source: GET /api/projects/:id (projectService.getScopedProject) — it
 * already includes documents, jhas and constructionPlans scoped to the
 * caller's company (plus, for the worker tier, an ACTIVE assignment). No
 * project-scoped incidents list exists: the InitialReport model has no
 * projectId, so the incidents section shows the entry action plus an honest
 * note — it never fabricates a count.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ClipboardCheck, FileText, Map, ShieldAlert, Download, Eye } from 'lucide-react';
import { api } from '@/utils/api';
import { canSeeTile, canCreatePlans, getStoredUser } from '@/utils/roleGate';
import { formatCalendarDate } from '@/utils/calendarDate';

interface PacketDocument {
  id: number;
  originalName?: string;
  name?: string;
  docType?: string;
  mimeType?: string;
  sizeBytes?: number;
}
interface PacketJHA {
  id: number;
  title?: string;
  workDescription?: string;
  date?: string;
  signedAt?: string | null;
  needsForemanReview?: boolean;
  crewMembers?: unknown;
}

/**
 * SOL-003 / LUNA-002 fix — sign-status TRUTH. The row-level JHA.signedAt is a
 * server creation timestamp (non-nullable in schema, always stamped), so it
 * can never mean "fully signed". Real sign-off truth lives per crew member
 * (crewMembers[].signature). needsForemanReview (worker-created emergency
 * JHAs) is the most important status and wins over everything.
 * DELTA-02 fix: legacy numeric rosters (and rows with no interpretable
 * roster) carry NO sign truth — the old fallback labeled them "signed" off
 * the creation stamp. They now say so honestly instead of claiming a
 * signature status the data cannot support.
 *
 * SOL-04: the truth function now lives in @/utils/jhaSignStatus so
 * JHADetailPage and this packet share ONE implementation; it is re-exported
 * here to keep existing imports (ProjectPacketView.test.tsx) intact.
 */
export { jhaSignStatus } from '@/utils/jhaSignStatus';
import { jhaSignStatus } from '@/utils/jhaSignStatus';
interface PacketPlan {
  id: number;
  title?: string;
  createdAt?: string;
}

/** Stream a document through the authenticated api client as a real download. */
export async function downloadPacketDocument(
  projectId: string,
  doc: PacketDocument,
  mode: 'download' | 'view'
): Promise<void> {
  const response = await api.get(`/projects/${projectId}/documents/${doc.id}/download`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data as BlobPart], {
    type: (response.headers?.['content-type'] as string) || doc.mimeType || 'application/octet-stream',
  });
  const url = URL.createObjectURL(blob);
  if (mode === 'view') {
    window.open(url, '_blank', 'noopener');
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.originalName ?? doc.name ?? `document-${doc.id}`;
    a.click();
  }
  // Give the browser a beat to start the fetch before revoking.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function ProjectPacketView() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<any>(null);
  const [error, setError] = useState('');
  const [docNotice, setDocNotice] = useState('');
  const viewer = getStoredUser();

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    // SOL-005 fix — in-place param change: clear the previous job's state
    // immediately (no stale packet under the new URL) and drop stale
    // responses (a slower old-id fetch must never overwrite the new job).
    setProject(null);
    setError('');
    setDocNotice('');
    api
      .get(`/projects/${id}`)
      .then((response: any) => {
        if (cancelled) return;
        setProject(response.data?.data ?? response.data);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load this job. Ask your supervisor to check your assignment.');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) {
    return (
      <main className="min-h-screen bg-gray-50 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <Link to="/my-jobs" className="text-sm text-blue-600 hover:underline">← My Jobs</Link>
          <p className="mt-4 text-red-600">{error}</p>
        </div>
      </main>
    );
  }
  if (!project) {
    return <main className="p-8 text-gray-600">Loading job...</main>;
  }

  const docs: PacketDocument[] = project.documents ?? [];
  const jhas: PacketJHA[] = project.jhas ?? project.JHA ?? [];
  const plans: PacketPlan[] = project.constructionPlans ?? project.plans ?? [];

  // Role truth: the same tiles DashboardPage uses, filtered to this job.
  const showJhas = canSeeTile(viewer.role, 'VIEW_JHAS');
  const showCreateJha = canSeeTile(viewer.role, 'CREATE_JHA');
  const showPlans = canSeeTile(viewer.role, 'CONSTRUCTION_PLANS');
  const showIncidents = canSeeTile(viewer.role, 'REPORT_INCIDENT');
  const planCta = canCreatePlans(viewer.role);
  // Documents: part of the job packet for every role that can see the job
  // (PRD §5.3 "documents — full set"). The DOCUMENT_LIBRARY tile is the
  // company-wide library (SAFETY_ONLY) — it does not gate a project's own
  // documents; the server download route enforces the assignment scope.

  const incidentHref = `/initial-reports/new?job=${encodeURIComponent(project.name ?? '')}&site=${encodeURIComponent(project.siteAddress ?? '')}`;

  return (
    <main className="min-h-screen bg-gray-50 px-6 py-8">
      <div className="max-w-7xl mx-auto">
        <Link to="/my-jobs" className="text-sm text-blue-600 hover:underline">← My Jobs</Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-4">{project.name}</h1>
        <p className="text-gray-600 mt-1">
          {project.siteAddress}
          {project.status ? <> · <span className="font-medium">{project.status}</span></> : null}
        </p>

        <section className="grid md:grid-cols-2 xl:grid-cols-3 gap-5 mt-8" data-testid="packet-sections">
          {showJhas && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-5" data-testid="packet-jhas">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-gray-900 inline-flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4 text-green-600" /> JHAs ({jhas.length})
                </h2>
                {showCreateJha && (
                  <Link
                    to={`/jha/create?projectId=${project.id}`}
                    className="text-sm font-medium text-blue-600 hover:underline"
                  >
                    + Create JHA for this job
                  </Link>
                )}
              </div>
              {jhas.length === 0 ? (
                <p className="text-sm text-gray-600">
                  No JHAs yet for this job —{' '}
                  {showCreateJha
                    ? 'use “Create JHA for this job” above before crew starts work.'
                    : 'ask your supervisor to complete one before work starts.'}
                </p>
              ) : (
                <ul className="space-y-2">
                  {jhas.map((jha) => (
                    <li key={jha.id} className="text-sm">
                      <Link to={`/jha/${jha.id}`} className="text-blue-600 hover:underline font-medium">
                        {jha.title ?? jha.workDescription ?? `JHA ${jha.id}`}
                      </Link>
                      <span className="text-gray-500">
                        {' '}
                        {jha.date ? `· ${formatCalendarDate(jha.date)}` : ''} · {jhaSignStatus(jha)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {showPlans && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-5" data-testid="packet-plans">
              <h2 className="font-semibold text-gray-900 mb-3 inline-flex items-center gap-2">
                <Map className="h-4 w-4 text-orange-600" /> Construction Plans ({plans.length})
              </h2>
              {plans.length === 0 ? (
                <p className="text-sm text-gray-600">
                  {planCta
                    ? 'No construction plan on file for this job yet — create one from the Construction Plans page before work starts.'
                    : 'No construction plan on file for this job yet — your supervisor adds plans; check back before work starts.'}
                </p>
              ) : (
                <ul className="space-y-2">
                  {plans.map((plan) => (
                    <li key={plan.id} className="text-sm">
                      <Link to={`/construction-plans/${plan.id}`} className="text-blue-600 hover:underline font-medium">
                        {plan.title ?? `Plan ${plan.id}`}
                      </Link>
                      <span className="text-gray-500"> · view plan</span>
                    </li>
                  ))}
                </ul>
              )}
              {!planCta && plans.length > 0 && (
                <p className="mt-3 text-xs text-gray-500">Plans are view-only from your job page.</p>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl shadow border border-gray-100 p-5" data-testid="packet-documents">
            <h2 className="font-semibold text-gray-900 mb-3 inline-flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" /> Documents ({docs.length})
            </h2>
            {docNotice && <p className="mb-3 text-sm text-red-600">{docNotice}</p>}
            {docs.length === 0 ? (
              <p className="text-sm text-gray-600">
                No documents on this job yet — your supervisor uploads plans, permits and scope docs; they appear here as soon as they exist.
              </p>
            ) : (
              <ul className="space-y-2">
                {docs.map((doc) => {
                  const label = doc.originalName ?? doc.name ?? `Document ${doc.id}`;
                  return (
                    <li key={doc.id} className="text-sm flex items-center justify-between gap-2">
                      <span className="text-gray-700 truncate">
                        {label}
                        {doc.docType ? <span className="text-gray-400"> · {doc.docType}</span> : null}
                      </span>
                      <span className="flex items-center gap-3 shrink-0">
                        <button
                          type="button"
                          aria-label={`View ${label}`}
                          className="text-gray-500 hover:text-blue-600"
                          onClick={() =>
                            downloadPacketDocument(String(project.id), doc, 'view').catch(() =>
                              setDocNotice(`Could not open ${label}. Try the Download button; if it still fails the file may be missing on the server.`)
                            )
                          }
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Download ${label}`}
                          className="text-blue-600 hover:text-blue-800"
                          onClick={() =>
                            downloadPacketDocument(String(project.id), doc, 'download').catch(() =>
                              setDocNotice(`Could not download ${label}. Refresh the page and try again; if it still fails the file may be missing on the server.`)
                            )
                          }
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {showIncidents && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-5" data-testid="packet-incidents">
              <h2 className="font-semibold text-gray-900 mb-3 inline-flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-red-600" /> Incidents
              </h2>
              <Link
                to={incidentHref}
                className="inline-flex items-center gap-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg px-3 py-2"
              >
                <ShieldAlert className="h-4 w-4" /> Report an incident for this job
              </Link>
              <p className="mt-3 text-xs text-gray-500">
                The report form opens with this job's name and site pre-filled. Incident reports are kept in the
                company incident log (they aren't filed under a single job yet), so this page doesn't show a
                per-job count — check the incident log for this job's reports.
              </p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
